import PropTypes from 'prop-types';
import { Box, Tooltip } from '@material-ui/core';
import placeholder_illustration from 'assets/img/placeholder_illustration.png';
import GeneralGrid from 'common/components/layout/grid/GeneralGrid';
import LoadingProgress from 'common/components/progress/loading';
import Table from 'common/components/table/table-material';
import React, { useEffect, useState, useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getList, setPage, fetchProductDetails } from './productsReportActions';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';

const exportToExcel = (csvData, fileName) => {
  const fileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';
  const ws = XLSX.utils.json_to_sheet(csvData);
  const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: fileType });
  saveAs(data, fileName + fileExtension);
};

// Função para gerar relatório de vouchers do produto
const exportVouchersReport = async (rowData) => {
  try {
    // Buscar dados completos do produto da API
    const productDetails = await fetchProductDetails(rowData.id);

    const dataAtual = new Date();
    const dia = dataAtual.getDate();
    const mes = dataAtual.getMonth() + 1;
    const ano = dataAtual.getFullYear();

    // Vouchers vêm de presential_course.presential_course_subscriptions
    const subscriptions = productDetails.presential_course?.presential_course_subscriptions || [];

    const vouchersData = subscriptions.map((pcs) => ({
      'Nome do Usuário': pcs.user?.name || 'N/A',
      CPF: pcs.user?.cpf || '',
      Telefone: pcs.user?.tel || '',
      'E-mail': pcs.user?.email || '',
      Voucher: pcs.code || '',
      Emissão: pcs.createdAt ? format(new Date(pcs.createdAt), 'dd/MM/yyyy HH:mm:ss') : '-',
      Validador: pcs.code_validator?.identification || '',
      Validação:
        pcs.validated === '1' && pcs.validation_date
          ? format(new Date(pcs.validation_date), 'dd/MM/yyyy HH:mm:ss')
          : '-',
      Status: pcs.validated === '1' ? 'Validado' : 'Pendente',
    }));

    if (vouchersData.length === 0) {
      alert('Não há vouchers gerados para este produto.');
      return;
    }

    const fileName = `vouchers_${productDetails.name || 'produto'}_${dia}_${mes}_${ano}`;
    exportToExcel(vouchersData, fileName);
  } catch (error) {
    console.error('Erro ao exportar vouchers:', error);
    alert('Erro ao exportar relatório de vouchers.');
  }
};

// Função para gerar relatório de vendas do produto
const exportSalesReport = async (rowData) => {
  try {
    // Buscar dados completos do produto da API
    const productDetails = await fetchProductDetails(rowData.id);

    const dataAtual = new Date();
    const dia = dataAtual.getDate();
    const mes = dataAtual.getMonth() + 1;
    const ano = dataAtual.getFullYear();

    // Vendas vêm de single_product_orders com user incluído diretamente
    const salesData = (productDetails.single_product_orders || []).map((spo) => ({
      'Nome do Cliente': spo.user?.name || 'N/A',
      CPF: spo.user?.cpf || '',
      Telefone: spo.user?.tel || '',
      'E-mail': spo.user?.email || '',
      Produto: productDetails.name || '',
      'Código do Pedido': spo.product_order?.code || '',
      Valor: productDetails.value || 0,
      'Local de Entrega': spo.product_order?.delivery_location?.name || '',
      Endereço: spo.product_order?.address || '',
      CEP: spo.product_order?.zipcode || '',
      'Data da Compra': spo.createdAt
        ? format(new Date(spo.createdAt), 'dd/MM/yyyy HH:mm:ss')
        : '-',
      Status: spo.product_order?.status || '',
    }));

    if (salesData.length === 0) {
      alert('Não há vendas para este produto.');
      return;
    }

    const fileName = `vendas_${productDetails.name || 'produto'}_${dia}_${mes}_${ano}`;
    exportToExcel(salesData, fileName);
  } catch (error) {
    console.error('Erro ao exportar vendas:', error);
    alert('Erro ao exportar relatório de vendas.');
  }
};

function ProductsReport(props) {
  const { auth } = props;
  const { content } = props.productsReport.list;
  const { pagination } = props.productsReport;
  const { router } = props;

  const [currentLimit, setCurrentLimit] = useState(5);
  const stationId = auth.user.establishments[0].stations[0].id;

  const defaultColumns = [
    {
      title: 'Produto',
      field: 'name',
    },
    {
      title: 'Atividade',
      field: 'action.name',
      render: (rowData) => rowData.action?.name,
    },
    {
      title: 'Início',
      field: 'action.start',
      render: (rowData) =>
        rowData.action?.start ? new Date(rowData.action.start).toLocaleDateString('pt-BR') : '-',
    },
    {
      title: 'Término',
      field: 'action.end',
      render: (rowData) =>
        rowData.action?.end ? new Date(rowData.action.end).toLocaleDateString('pt-BR') : '-',
    },
    {
      title: 'Views da Atividade',
      field: 'action_views_count',
      render: (rowData) => rowData.action_views_count || 0,
    },
    {
      title: 'Estoque inicial',
      field: 'stock',
    },
    {
      title: 'Qtd. de Vendas',
      field: 'sales_count',
      render: (rowData) => rowData.sales_count || 0,
    },
    {
      title: 'Vouchers Gerados',
      field: 'vouchers_gerados',
      render: (rowData) => rowData.vouchers_gerados || 0,
    },
    {
      title: 'Estoque disponível',
      field: 'available_stock',
      render: (rowData) => (rowData.stock || 0) - (rowData.sales_count || 0),
    },
    {
      title: 'Status',
      field: 'action.active',
      render: (rowData) =>
        !rowData.action.active ? 'Atividade fora do ar' : rowData.deletedAt ? 'Esgotado' : 'Online',
    },
    {
      title: 'Relatórios',
      field: 'reports',
      render: (rowData) => (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Tooltip title="Baixar relatório de Vouchers" placement="top">
            <button
              onClick={(e) => {
                e.stopPropagation();
                exportVouchersReport(rowData);
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              aria-label="Baixar relatório de Vouchers">
              <FontAwesomeIcon icon={faBook} style={{ color: 'blue', fontSize: '18px' }} />
            </button>
          </Tooltip>
          <Tooltip title="Baixar relatório de Vendas" placement="top">
            <button
              onClick={(e) => {
                e.stopPropagation();
                exportSalesReport(rowData);
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              aria-label="Baixar relatório de Vendas">
              <FontAwesomeIcon icon={faShoppingCart} style={{ color: 'green', fontSize: '18px' }} />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];
  function onRowClick(e, rowdata) {
    props.router.push(`/owner/relatorio-produtos/${rowdata.id}`);
  }
  const columns = defaultColumns;

  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const getFilteredData = () => {
    if (!searchValue) return data;
    const lowerSearch = searchValue.toLowerCase();
    return data.filter((item) => {
      const productName = (item.name || '').toLowerCase();
      const actionName = (item.action?.name || '').toLowerCase();
      return productName.includes(lowerSearch) || actionName.includes(lowerSearch);
    });
  };

  const handleExportClick = () => {
    const filteredData = getFilteredData();
    const exportData = filteredData.map((item) => ({
      Produto: item.name || '',
      Atividade: item.action?.name || '',
      Início: item.action?.start ? new Date(item.action.start).toLocaleDateString('pt-BR') : '-',
      Término: item.action?.end ? new Date(item.action.end).toLocaleDateString('pt-BR') : '-',
      'Views da Atividade': item.action_views_count || 0,
      'Estoque Inicial': item.stock || 0,
      'Qtd. de Vendas': item.sales_count || 0,
      'Vouchers Gerados': item.vouchers_gerados || 0,
      'Estoque Disponível': (item.stock || 0) - (item.sales_count || 0),
      Status: !item.action?.active
        ? 'Atividade fora do ar'
        : item.deletedAt
        ? 'Esgotado'
        : 'Online',
    }));
    exportToExcel(exportData, 'relatorio_produtos');
  };

  // Função para buscar dados com paginação
  const fetchData = useCallback(
    (page = 1, limit = currentLimit, search = '') => {
      props.getList(stationId, page, limit, search);
    },
    [stationId, currentLimit]
  );

  // Função chamada quando a página muda
  const handlePageChange = useCallback(
    (newPage) => {
      props.setPage(newPage);
      fetchData(newPage, currentLimit);
    },
    [fetchData, currentLimit]
  );

  // Função chamada quando o limite por página muda
  const handleLimitChange = useCallback(
    (newLimit) => {
      setCurrentLimit(newLimit);
      props.setPage(1);
      fetchData(1, newLimit);
    },
    [fetchData]
  );

  useEffect(() => {
    // Limpa a página salva ao montar o componente para começar sempre da página 1
    sessionStorage.removeItem('currentPage');
    fetchData(1, currentLimit);
  }, []);

  useEffect(() => {
    setData(props.productsReport.list.content);
  }, [props.productsReport.list.content]);

  function renderEmptyScreen() {
    return (
      <section id="productsReport">
        <GeneralGrid container spacing={3} alignItems="center">
          <GeneralGrid item xs={12} md={6}>
            <GeneralGrid container spacing={3}>
              <GeneralGrid item xs={12}>
                <div className="pl-4">
                  <h2 className="signup-text">Você ainda não possui produtos</h2>
                </div>
              </GeneralGrid>
            </GeneralGrid>
          </GeneralGrid>
          <GeneralGrid item xs={12} md={6}>
            <img alt="" src={placeholder_illustration} className="img-fluid" />
          </GeneralGrid>
        </GeneralGrid>
      </section>
    );
  }

  if (content.length < 1 && !props.productsReport.loading) {
    return renderEmptyScreen();
  }
  return (
    <section>
      <GeneralGrid container spacing={3}>
        <GeneralGrid item xs={12}>
          <GeneralGrid container spacing={3}>
            <GeneralGrid item xs={12}>
              <Table
                hasToolbar
                toolbarSearch
                title="Lista de Produtos"
                columns={columns}
                rows={data}
                onRowClick={onRowClick}
                router={router}
                mobileTable={isMobile}
                hasBorder={!isMobile}
                buttonCSV
                onExportClick={handleExportClick}
                searchBarOnChange={(value) => setSearchValue(value)}
                setAPIPage={handlePageChange}
                setAPILimit={handleLimitChange}
                countFromAPI={pagination.total}
                defaultRowsPerPage={currentLimit}
                loading={props.productsReport.loading ? 'pending' : false}
              />
            </GeneralGrid>
          </GeneralGrid>
        </GeneralGrid>
      </GeneralGrid>
    </section>
  );
}

ProductsReport.propTypes = {
  auth: PropTypes.shape({
    user: PropTypes.shape({
      establishments: PropTypes.any,
    }),
  }),
  getList: PropTypes.func,
  setPage: PropTypes.func,
  productsReport: PropTypes.shape({
    list: PropTypes.shape({
      content: PropTypes.shape({
        length: PropTypes.number,
      }),
    }),
    pagination: PropTypes.shape({
      page: PropTypes.number,
      limit: PropTypes.number,
      total: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    loading: PropTypes.any,
  }),
  router: PropTypes.shape({
    push: PropTypes.func,
  }),
};

const mapStateToProps = (state) => {
  return {
    productsReport: state.productsReport,
    auth: state.auth,
    general: state.general,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({ getList, setPage }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductsReport);
